'use client';
import { Picture } from '@petsmart-ui/sparky';
import { A11y, FreeMode, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { trackContentfulClick } from '@libs/analytics';
import { BREAKPOINTS } from '@libs/common-next';

import { COLOR_TABLE } from '../../constants';
import type { Link } from '../../types';
import { calculateBreakpointConfiguration } from './Tile6UpCampaign.utils';
import type { Tile6UpProps, TilesProps } from './types';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import './Tile6UpCampaign.scss';

type CampaignTileProps = Omit<TilesProps, 'link'> & {
    link: Link;
};

export default function Tile6UpCampaign({ header, variant, tiles, pathname, textColor }: Readonly<Tile6UpProps>) {
    const campaignTiles: CampaignTileProps[] = tiles.filter((tile): tile is CampaignTileProps =>
        Boolean(tile.link?.webUrl)
    );

    if (campaignTiles.length === 0) {
        return null;
    }

    const handleTileClick = (title: string, index: number, link: string, internalTitle: string) => () => {
        trackContentfulClick({
            position: index + 1,
            linkText: title,
            linkUrl: link,
            promoTitle: header ?? '',
            promoVariant: `tile 6up - ${variant.toLowerCase()}`,
            promoOrigin: pathname,
            promoDesc: internalTitle,
            promoType: `tile-6up-${variant.toLowerCase()}`,
        });
    };

    return (
        <div className="tile6up-campaign-wrapper">
            <Swiper
                spaceBetween={32}
                slidesPerView="auto"
                modules={[FreeMode, Navigation, A11y]}
                freeMode
                breakpoints={calculateBreakpointConfiguration(campaignTiles.length)}
                navigation={true}
                a11y={{
                    enabled: true,
                    scrollOnFocus: true,
                }}
            >
                {campaignTiles.map((tile: CampaignTileProps, index: number) => {
                    return (
                        <SwiperSlide
                            key={tile.sysId || `${tile.title}-${index}-${tile.image?.remoteDesktopImageUrl || ''}`}
                            className="tile6up-campaign-slide"
                        >
                            <a
                                className="sparky-c-text-link sparky-c-tile six-tile-item"
                                href={tile.link.webUrl}
                                onClick={handleTileClick(tile.title, index, tile.link.webUrl, tile.internalTitle || '')}
                                data-testid="six-tile-carousel-tile"
                            >
                                <Picture
                                    imgSrc={tile.image.remoteDesktopImageUrl}
                                    imgAlt={tile.image.webAltText}
                                    items={[
                                        {
                                            media: `(max-width: ${BREAKPOINTS.md}px)`,
                                            srcSet: tile.image.remoteMobileImageUrl ?? tile.image.remoteDesktopImageUrl,
                                        },
                                        {
                                            media: `(min-width: ${BREAKPOINTS.md}px)`,
                                            srcSet: tile.image.remoteDesktopImageUrl,
                                        },
                                    ]}
                                />
                                <span
                                    className="sparky-c-tile__title"
                                    style={{ color: textColor ? COLOR_TABLE[textColor] : undefined }}
                                >
                                    {tile.title}
                                </span>
                            </a>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
}
